import { useRef, useEffect } from 'react';
import { Element, ROOT_NODE, useEditor, useNode, type Node } from '@craftjs/core';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';
import { selectTranslateX, selectTranslateY } from '@/store/zoom/selectors';
import EditorContainer from '@/components/userComponents/Container';
import type { ContainerProps } from '@builder4/shared/components/Container';
import IconAdd from '@/assets/icons/icon-add.svg?react';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { getDomFixedPosition, isRelativelyFillableElementV2 } from '@/utils/helperFunc';
import { useContainerHelper } from '@/hooks/useContainerHelper';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { getBreakpointProp, setBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import { CONTENT_BLOCK_NAME } from '@/utils/constants';

const AddContainer = () => {
  const { getInitialPlaceholderHeight, getIndexInSiblings } = useCraftHelper();
  const { equallyDivideParentSpace, hasEquallyDividedSpace, distributeSpaceOnNewObjectAdd } =
    useContainerHelper();
  const { query, actions } = useEditor();
  const {
    id,
    parent,
    dom,
    parentNode,
    isDragged,
    nodeName,
    props: nodeProps,
  } = useNode((node) => {
    return {
      dom: node.dom,
      nodeName: node.data.displayName,
      parent: node.data.parent,
      props: node.data.props as ContainerProps,
      parentNode: query.node(node.data.parent as string).get(),
      isDragged: node.events.dragged,
    };
  });
  const translateX = useAppSelector(selectTranslateX);
  const translateY = useAppSelector(selectTranslateY);
  const rootDom = useRef<HTMLDivElement>(null);
  const breakpoint = useAppSelector(selectBreakpoint);

  // Get breakpoint-aware prop functions for current node
  const { getBPProp } = useBreakpointAwareProp(nodeName);

  // Read position props through breakpoint-aware system
  const left = getBPProp(nodeProps, 'left');
  const top = getBPProp(nodeProps, 'top');
  useEffect(() => {
    if (rootDom.current) {
      const { right: domRight, bottom: domBottom, centerX, centerY } = getDomFixedPosition(dom);

      rootDom.current.style.top = getParentDirection() === 'column' ? domBottom : centerY;
      rootDom.current.style.left = getParentDirection() === 'column' ? centerX : domRight;
    }
  }, [translateX, translateY, left, top, isDragged, breakpoint, parentNode]);

  const getParentDirection = () => {
    if (parentNode.id === ROOT_NODE) {
      return 'column';
    } else {
      return getBPProp(parentNode.data.props, 'flexDirection');
    }
  };

  const handleAddContainerToRoot = () => {
    if (parent == ROOT_NODE) {
      const newNode = query.createNode(
        <Element
          canvas={true}
          is={EditorContainer}
          flexDirection="row"
          flexGrow={1}
          fillSpace="yes"
          disableMaxWidth={true}
        />,
      );
      const index = getIndexInSiblings(id, parent as string);
      actions.add(newNode, parent as string, index + 1);
      actions.selectNode(newNode.id);
    } else {
      handleAddContainer();
    }
  };

  const handleAddContainer = () => {
    const parentId = parent as string;
    const parentNode = query.node(parentId as string)?.get();
    const node = query.node(id).get();

    const index = getIndexInSiblings(id, parentId as string);

    const defaultProps = {
      flexDirection: parentId !== ROOT_NODE ? 'column' : 'row',
    } as Partial<ContainerProps>;

    const placeholderHeight = getInitialPlaceholderHeight(parentId as string);
    if (placeholderHeight) {
      defaultProps.placeholderHeight = placeholderHeight;
    }
    const newNodeTree = query
      .parseReactElement(
        <Element
          canvas={true}
          is={EditorContainer}
          {...defaultProps}
          disableMaxWidth={parentId === ROOT_NODE}
        />,
      )
      .toNodeTree();

    const breakpointSpecificSpaceDivision = {
      desktop: hasEquallyDividedSpace(parentId as string, 'desktop'),
      tablet: hasEquallyDividedSpace(parentId as string, 'tablet'),
      mobile: hasEquallyDividedSpace(parentId as string, 'mobile'),
    };

    //adding after checking equally divided space as we need to decide width after adding
    actions.addNodeTree(newNodeTree, parentId as string, index + 1);
    actions.selectNode(newNodeTree.rootNodeId);

    if (parentId === ROOT_NODE) {
      return;
    }

    (['desktop', 'tablet', 'mobile'] as const).forEach((bp) => {
      //add container using base props
      const parentFlexDirection = getBreakpointProp(
        parentNode.data.props as ContainerProps,
        'flexDirection',
        bp,
      );

      actions.history
        .merge()
        .setProp(newNodeTree.rootNodeId as string, (props: Partial<ContainerProps>) => {
          setBreakpointProp(
            props,
            'fillSpace',
            parentFlexDirection === 'row' ? 'yes' : 'no',
            bp,
            CONTENT_BLOCK_NAME,
          );
        });

      if (parentFlexDirection === 'column' || !isRelativelyFillableElementV2(node as Node, bp)) {
        return;
      }

      if (breakpointSpecificSpaceDivision[bp]) {
        equallyDivideParentSpace(parentId as string, bp);
      } else {
        distributeSpaceOnNewObjectAdd(parentId as string, newNodeTree.rootNodeId as string, bp);
      }
    });
  };

  return (
    <div
      onClick={handleAddContainerToRoot}
      className="flex h-7 w-7 cursor-pointer items-center justify-center gap-1 rounded-full bg-indigo-500 px-2 py-1.5"
    >
      <IconAdd className="h-4 w-4 text-white" />
    </div>
  );
};
export default AddContainer;
